import React, { useState, useEffect } from 'react';
import { Plus, Search, Car, Clock, Trash2, X, Calendar } from 'lucide-react';
import { Customer } from '../lib/customers';
import { CustomerVehicle } from '../lib/customers';
import { Service, ServiceCategory } from '../lib/services';
import { createProposal, Proposal } from '../lib/proposals';
import { useBusiness } from '../contexts/BusinessContext';
import EditCustomerModal from './manage/EditCustomerModal';
import SelectCustomerModal from './manage/SelectCustomerModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import { sendProposalLink } from '../lib/proposals';
import { useToast } from '../components/ui/use-toast';
import { useNavigate } from 'react-router-dom';

interface ServiceOption {
  id: string;
  vehicleId: string;
  vehicleName: string;
  name: string;
  originalName: string;
  description: string;
  originalDescription: string;
  priceOptionId: string;
  price: number;
  originalPrice: number;
  duration_minutes: number;
  isSelectable?: boolean;
}

interface Offer {
  title: string;
  services: ServiceOption[];
}

interface CreateProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (createdProposal: Proposal) => void;
}

export default function CreateProposalModal({
  isOpen,
  onClose,
  onSuccess
}: CreateProposalModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('');
  const [expiryOption, setExpiryOption] = useState('30');
  const [customExpiryDate, setCustomExpiryDate] = useState('');
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [showOfferSection, setShowOfferSection] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [selectedServices, setSelectedServices] = useState<ServiceOption[]>([]);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [selectedPriceOptions, setSelectedPriceOptions] = useState<Record<string, string>>({});
  const [offers, setOffers] = useState<Offer[]>([]);
  const [currentOffer, setCurrentOffer] = useState<Offer | null>(null);
  const [offerTitle, setOfferTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isCustomerValid, setIsCustomerValid] = useState(false);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      setTitle('Proposal');
      setExpiryOption('30');
      setCustomExpiryDate('');
      setShowCustomDate(false);
    } else {
      document.body.style.overflow = 'unset';
      // Reset form when modal closes
      setTitle('');
      setExpiryOption('30');
      setCustomExpiryDate('');
      setShowCustomDate(false);
      setSelectedCustomer(null);
      setShowOfferSection(false);
      setSelectedServices([]);
      setCurrentVehicle(null);
      setOffers([]);
      setCurrentOffer(null);
      setOfferTitle('');
      setError(null);
    }
    setShowValidationMessages(false);
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Add this useEffect right after the existing useEffect blocks to handle the selectable service flag
  useEffect(() => {
    // Check and clean up localStorage when modal opens or closes
    if (!isSelectServiceModalOpen) {
      localStorage.removeItem('adding_selectable_service');
    }
  }, [isSelectServiceModalOpen]);

  const handleAddService = (service: Service, priceOption: any, isSelectable: boolean = false) => {
    if (!currentVehicle) return;
    
    const newService = {
      id: service.id,
      vehicleId: currentVehicle.id,
      vehicleName: `${currentVehicle.year} ${currentVehicle.make} ${currentVehicle.model}`,
      name: isSelectable ? `${service.name} (Optional)` : service.name,
      originalName: service.name,
      description: service.description || '',
      originalDescription: service.description || '',
      priceOptionId: priceOption.id,
      price: priceOption.price,
      originalPrice: priceOption.price,
      duration_minutes: priceOption.duration_minutes,
      isSelectable: isSelectable
    };

    setSelectedServices(prev => [...prev, newService]);
    setIsSelectServiceModalOpen(false);
  };

  const handleRemoveService = (index: number) => {
    setSelectedServices(prev => prev.filter((_, i) => i !== index));
  };

  const handlePriceChange = (index: number, newPrice: number) => {
    setSelectedServices(prev => prev.map((service, i) => 
      i === index ? { ...service, price: newPrice } : service
    ));
  };

  const handleServiceNameChange = (index: number, newName: string) => {
    setSelectedServices(prev => prev.map((service, i) => 
      i === index ? { ...service, name: newName } : service
    ));
  };

  const handleServiceDescriptionChange = (index: number, newDescription: string) => {
    setSelectedServices(prev => prev.map((service, i) => 
      i === index ? { ...service, description: newDescription } : service
    ));
  };

  const handleAddOffer = () => {
    setShowOfferSection(true);
    setOfferTitle('');
    setSelectedServices([]);
    setCurrentVehicle(null);
  };

  const handleEditOffer = (offer: Offer) => {
    setCurrentOffer(offer);
    setOfferTitle(offer.title);
    setSelectedServices(offer.services);
    if (offer.services.length > 0) {
      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === offer.services[0].vehicleId);
      setCurrentVehicle(vehicle || null);
    }
    setShowOfferSection(true);
  };

  const handleSaveOffer = () => {
    if (offerTitle && selectedServices.length > 0) {
      if (currentOffer) {
        setOffers(prev => prev.map(offer => 
          offer === currentOffer 
            ? {
              title: offerTitle,
              services: selectedServices
            }
            : offer
        ));
      } else {
        setOffers(prev => [...prev, {
          title: offerTitle,
          services: selectedServices
        }]);
      }
      setShowOfferSection(false);
      setOfferTitle('');
      setSelectedServices([]);
      setCurrentVehicle(null);
      setCurrentOffer(null);
    }
  };

  const handleRemoveOffer = (index: number) => {
    setOffers(prev => prev.filter((_, i) => i !== index));
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Update customer validation when customer changes
  useEffect(() => {
    setIsCustomerValid(!!selectedCustomer);
  }, [selectedCustomer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidationMessages(true);

    if (!selectedBusiness?.id || !isCustomerValid) {
      setError('Please select a customer');
      return;
    }

    if (offers.length === 0) {
      setError('Please add at least one offer');
      return;
    }

    // Calculate expiry date based on selection
    let expiryDate = new Date();
    if (showCustomDate && customExpiryDate) {
      expiryDate = new Date(customExpiryDate);
    } else {
      expiryDate.setDate(expiryDate.getDate() + parseInt(expiryOption));
    }

    try {
      setLoading(true);
      setError(null);

      const createdProposal = await createProposal({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer!.id,
        title,
        valid_until: expiryDate.toISOString(),
        offers: offers.map(offer => ({
          title: offer.title,
          services: offer.services.map(service => ({
            service_id: service.id,
            vehicle_id: service.vehicleId,
            name: service.name,
            description: service.description || '',
            price: service.price,
            tax_exempt: false,
            tax_rate: 5,
            duration_minutes: service.duration_minutes,
            isSelectable: service.isSelectable || false
          }))
        }))
      });

      // Pass the created proposal to the onSuccess callback
      onSuccess?.(createdProposal);
      onClose();

      toast({
        title: 'Proposal Created',
        description: `Proposal "${title}" created successfully!`,
        duration: 3000, // 3 seconds
      });
    } catch (err) {
      console.error('Error creating proposal:', err);
      setError(err instanceof Error ? err.message : 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">New Proposal</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Title Section */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h1 className="text-xl font-semibold text-gray-900">New Proposal</h1>
                </div>
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter proposal title"
                      className="w-full pl-4 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expiry Date
                      </label>
                      <select
                        value={expiryOption}
                        onChange={(e) => {
                          setExpiryOption(e.target.value);
                          setShowCustomDate(e.target.value === 'custom');
                        }}
                        className="w-full pl-4 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      >
                        <option value="60">60 days from now</option>
                        <option value="30">30 days from now</option>
                        <option value="15">15 days from now</option>
                        <option value="custom">Custom date</option>
                      </select>
                    </div>
                    
                    {showCustomDate && (
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Custom Date
                        </label>
                        <input
                          type="date"
                          value={customExpiryDate}
                          onChange={(e) => setCustomExpiryDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full pl-4 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Selection Section */}
              <div>
                {selectedCustomer ? (
                    <div className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100">
                      <div className="flex items-center space-x-4">
                        <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white">
                          {selectedCustomer.first_name[0]}{selectedCustomer.last_name[0]}
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-medium text-gray-900">
                            {selectedCustomer.first_name} {selectedCustomer.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {selectedCustomer.email}
                          </div>
                        </div>
                      </div> 
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsEditCustomerModalOpen(true);
                          }}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setIsSelectCustomerModalOpen(true)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsSelectCustomerModalOpen(true)}
                    className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Select Customer
                  </button>
                )}

                {/* Offers Section */}
                {selectedCustomer && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Offers</h3>

                    {/* Existing Offers List */}
                    {offers.length > 0 && 
                      <div className="mb-6">
                        <div className="space-y-4">
                          {offers.map((offer, index) => (
                            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium text-gray-900">{offer.title}</h4>
                                <div className="flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleEditOffer(offer);
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800 mr-2"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const newOffer = {
                                        title: `${offer.title} (Copy)`,
                                        services: offer.services.map(service => ({
                                          ...service,
                                          id: service.id
                                        }))
                                      };
                                      setOffers(prev => [...prev, newOffer]);
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800 mr-2"
                                    type="button"
                                  >
                                    Duplicate
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleRemoveOffer(index);
                                    }}
                                    className="text-xs text-red-600 hover:text-red-800"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-2">
                                {/* Display regular services first */}
                                {offer.services
                                  .filter(service => !service.isSelectable)
                                  .map((service, serviceIndex) => (
                                  <div key={`preview-regular-${serviceIndex}`} className="text-sm text-gray-500">
                                    <span>• {service.name} - ${service.price.toLocaleString()} ({service.vehicleName})</span>
                                  </div>
                                ))}
                                
                                {/* Show a divider if there are both regular and optional services */}
                                {offer.services.some(s => !s.isSelectable) && offer.services.some(s => s.isSelectable) && (
                                  <div className="text-xs text-gray-400 pt-1 border-t border-gray-200/50 mt-1">Optional Services</div>
                                )}
                                
                                {/* Display optional services next */}
                                {offer.services
                                  .filter(service => service.isSelectable)
                                  .map((service, serviceIndex) => (
                                  <div key={`preview-optional-${serviceIndex}`} className="text-sm text-gray-500">
                                    <div className="flex items-center">
                                      <div className="w-3 h-3 mr-1 border border-gray-300 rounded-sm bg-gray-200"></div>
                                      <span className="text-gray-400">• {service.name} - ${service.price.toLocaleString()} (Optional)</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    }
                    
                    {!showOfferSection && (
                      <button
                        type="button"
                        onClick={handleAddOffer}
                        className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        <Plus className="h-4 w-4 mr-2 inline-block" />
                        Create Offer
                      </button>
                    )}

                    {/* Offer Creation/Edit Section */}
                    {showOfferSection && (
                      <div className="mt-6 p-4 bg-white rounded-lg border-2 border-gray-900">
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Offer Title
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={offerTitle}
                              onChange={(e) => setOfferTitle(e.target.value)}
                              placeholder="Enter offer title"
                              className="w-full pl-4 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* Vehicle Selection */}
                        {!currentVehicle ? (
                          <button
                            type="button"
                            onClick={() => setIsSelectVehicleModalOpen(true)} 
                            className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                          >
                            Select Vehicle
                          </button>
                        ) : (
                          <div className="space-y-4">
                            {/* Group services by vehicle */}
                            {Object.entries(
                              selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
                                if (!acc[service.vehicleId]) {
                                  acc[service.vehicleId] = [];
                                }
                                acc[service.vehicleId].push(service);
                                return acc;
                              }, {})
                            ).map(([vehicleId, vehicleServices]) => (
                              <div key={vehicleId} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center space-x-3">
                                    <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                      <Car className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {vehicleServices[0].vehicleName}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Services for this vehicle */}
                                <div className="space-y-4">
                                  {/* Display regular services first */}
                                  {vehicleServices
                                    .filter(service => !service.isSelectable)
                                    .map((service, index) => (
                                    <div key={`regular-${index}`} className="bg-white rounded-lg p-3 border border-gray-200">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <input
                                            type="text"
                                            value={service.name}
                                            onChange={(e) => handleServiceNameChange(
                                              selectedServices.findIndex(s => s === service),
                                              e.target.value
                                            )}
                                            className="text-sm font-medium text-gray-900 border-none bg-transparent focus:ring-0 w-full"
                                            placeholder="Enter service name"
                                          />
                                          <textarea
                                            value={service.description}
                                            onChange={(e) => handleServiceDescriptionChange(
                                              selectedServices.findIndex(s => s === service),
                                              e.target.value
                                            )}
                                            className="mt-1 text-sm text-gray-500 w-full border-none bg-transparent focus:ring-0 resize-none"
                                            placeholder="Enter service description"
                                            rows={2}
                                          />
                                          <div className="mt-1 flex items-center text-xs text-gray-500">
                                            <Clock className="h-3.5 w-3.5 mr-1" />
                                            {service.duration_minutes >= 1440 
                                              ? `${Math.round(service.duration_minutes / 1440)} days`
                                              : `${Math.round(service.duration_minutes / 60)} hours`
                                            }
                                          </div>
                                          
                                          <div className="mt-2 flex items-center space-x-2">
                                            <div className="relative">
                                              <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500">$</span>
                                              <input
                                                type="number"
                                                value={service.price}
                                                onChange={(e) => handlePriceChange(
                                                  selectedServices.findIndex(s => s === service),
                                                  parseFloat(e.target.value) || 0
                                                )}
                                                className="pl-6 pr-2 py-1 w-32 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                                              />
                                            </div>
                                            {service.price !== service.originalPrice && (
                                              <span className="text-xs text-gray-500">
                                                Original: ${service.originalPrice.toLocaleString()}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveService(selectedServices.findIndex(s => s === service))}
                                          className="p-1 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}

                                  {/* Display selectable (optional) services next */}
                                  {vehicleServices.filter(service => service.isSelectable).length > 0 && (
                                    <div className="border-t border-gray-200 pt-3 mt-3 mb-2">
                                      <div className="text-center text-sm text-gray-600 font-medium bg-gray-50 py-2 rounded-md border border-gray-200">
                                        Optional Add-on Services
                                      </div>
                                    </div>
                                  )}
                                  
                                  {vehicleServices
                                    .filter(service => service.isSelectable)
                                    .map((service, index) => (
                                    <div key={`optional-${index}`} className="bg-white rounded-lg p-3 border border-gray-200 opacity-70">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="mb-2 flex items-center">
                                            <div className="w-10 h-5 bg-gray-200 rounded-full p-1 mr-2">
                                              <div className="bg-gray-400 w-3 h-3 rounded-full"></div>
                                            </div>
                                            <span className="text-xs text-gray-500">Optional Add-on</span>
                                          </div>
                                          <input
                                            type="text"
                                            value={service.name}
                                            onChange={(e) => handleServiceNameChange(
                                              selectedServices.findIndex(s => s === service),
                                              e.target.value
                                            )}
                                            className="text-sm font-medium text-gray-900 border-none bg-transparent focus:ring-0 w-full"
                                            placeholder="Enter service name"
                                          />
                                          <textarea
                                            value={service.description}
                                            onChange={(e) => handleServiceDescriptionChange(
                                              selectedServices.findIndex(s => s === service),
                                              e.target.value
                                            )}
                                            className="mt-1 text-sm text-gray-500 w-full border-none bg-transparent focus:ring-0 resize-none"
                                            placeholder="Enter service description"
                                            rows={2}
                                          />
                                          <div className="mt-1 flex items-center text-xs text-gray-500">
                                            <Clock className="h-3.5 w-3.5 mr-1" />
                                            {service.duration_minutes >= 1440 
                                              ? `${Math.round(service.duration_minutes / 1440)} days`
                                              : `${Math.round(service.duration_minutes / 60)} hours`
                                            }
                                          </div>
                                          
                                          <div className="mt-2 flex items-center space-x-2">
                                            <div className="relative">
                                              <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500">$</span>
                                              <input
                                                type="number"
                                                value={service.price}
                                                onChange={(e) => handlePriceChange(
                                                  selectedServices.findIndex(s => s === service),
                                                  parseFloat(e.target.value) || 0
                                                )}
                                                className="pl-6 pr-2 py-1 w-32 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                                              />
                                            </div>
                                            {service.price !== service.originalPrice && (
                                              <span className="text-xs text-gray-500">
                                                Original: ${service.originalPrice.toLocaleString()}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveService(selectedServices.findIndex(s => s === service))}
                                          className="p-1 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Add Service Button for this vehicle */}
                                <div className="mt-4 flex space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === vehicleId);
                                      if (vehicle) {
                                        setCurrentVehicle(vehicle);
                                        setIsSelectServiceModalOpen(true);
                                      }
                                    }}
                                    className="flex-1 flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Service
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === vehicleId);
                                      if (vehicle) {
                                        setCurrentVehicle(vehicle);
                                        localStorage.setItem('adding_selectable_service', 'true');
                                        setIsSelectServiceModalOpen(true);
                                      }
                                    }}
                                    className="flex-1 flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Selectable Service
                                  </button>
                                </div>
                              </div>
                            ))}

                            {/* Add Another Vehicle Button */}
                            <button
                              type="button"
                              onClick={() => setIsSelectVehicleModalOpen(true)}
                              className="mt-4 w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Another Vehicle
                            </button>
                          </div>
                        )}

                        {/* Pricing Summary */}
                        {selectedServices.length > 0 && (
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="text-sm font-medium text-gray-900 mb-4">Pricing Summary</h3>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm text-gray-600">
                                <span>Subtotal</span>
                                <span>${selectedServices.reduce((sum, service) => sum + service.price, 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-sm text-gray-600">
                                <span>GST (5%)</span>
                                <span>+ ${(selectedServices.reduce((sum, service) => sum + service.price, 0) * 0.05).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-sm font-medium text-gray-900 pt-2 border-t border-gray-200">
                                <span>Total</span>
                                <span>${(selectedServices.reduce((sum, service) => sum + service.price, 0) * 1.05).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="mt-6 flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowOfferSection(false);
                              setOfferTitle('');
                              setSelectedServices([]);
                              setCurrentVehicle(null);
                              setCurrentOffer(null);
                            }}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveOffer}
                            disabled={!offerTitle || selectedServices.length === 0}
                            className="px-4 py-2 text-sm text-white bg-gray-900 hover:bg-gray-800 rounded-md disabled:opacity-50"
                          >
                            Save Offer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Create Proposal Button - Always visible */}
            <div className="mt-8 space-y-3">
              <button
                type="submit"
                disabled={loading || !title || !isCustomerValid || (!showOfferSection && offers.length === 0)}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Proposal'}
              </button>
              {showValidationMessages && !isCustomerValid && (
                <p className="mt-2 text-xs text-center text-gray-500">Please select a customer</p>
              )}
              {showValidationMessages && isCustomerValid && !showOfferSection && offers.length === 0 && (
                <p className="mt-2 text-xs text-center text-gray-500">Please add at least one offer</p>
              )}
            </div>
          </form>
        </div>
      </div>

      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={(customer) => {
          setSelectedCustomer(customer);
          setIsSelectCustomerModalOpen(false);
        }} 
      />

      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setSelectedCustomer(updatedCustomer);
          setIsEditCustomerModalOpen(false);
        }}
        customer={selectedCustomer}
      />

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={(vehicle) => {
          setCurrentVehicle(vehicle);
          setIsSelectServiceModalOpen(true);
          setIsSelectVehicleModalOpen(false); 
        }}
        vehicles={selectedCustomer?.vehicles || []}
        selectedVehicles={selectedServices.map(s => ({
          id: s.vehicleId,
          make: s.vehicleName.split(' ')[1] || '',
          model: s.vehicleName.split(' ')[2] || '',
          year: parseInt(s.vehicleName.split(' ')[0]) || new Date().getFullYear()
        }))}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={(service, priceOption) => {
          if (currentVehicle) {
            const isSelectable = localStorage.getItem('adding_selectable_service') === 'true';
            handleAddService(service, priceOption, isSelectable);
            localStorage.removeItem('adding_selectable_service');
          }
        }}
      />
    </div>
  );
}